import { useEffect, useState, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { debounce } from 'lodash'
import { Dropdown, SearchElement, type IDropdownProps } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { NumbersDropdownContent } from 'widgets/SearchDropdown/ui/DropdownContent/NumbersDropdownContent/NumbersDropdownContent'
import { type SearchDropdownNumbersStore } from 'widgets/SearchDropdown/store/searchDropdownNumbersStore'
import styles from './styles.module.scss'

type NumbersElementProps = {
  parentsDropdownId?: string[]
  store: SearchDropdownNumbersStore
  onRemove: (type: SearchTypesEnum) => void
  activeType: SearchTypesEnum | null
  canShowChildDropdown: boolean
  dropdownProps?: Partial<IDropdownProps>
}

export const NumbersElement: FC<NumbersElementProps> = observer(
  ({ parentsDropdownId, store, onRemove, activeType, canShowChildDropdown, dropdownProps }) => {
    const [show, setShow] = useState(false)
    const { numbersStore } = useEntitiesContext()
    const { hasSelectedItems, selectedItemsCount, firstSelectedItemId, disableClosing } = store

    const NumbersLabel = () => {
      const firstNumber = numbersStore.getItem(firstSelectedItemId)

      if (hasSelectedItems && selectedItemsCount === 1 && firstNumber) {
        return (
          <div className={styles.countLabelWrapper}>
            <span className={styles.label}>{firstNumber.formatted_number}</span>
          </div>
        )
      }

      return (
        <span className={styles.countLabelWrapper}>
          <span className={styles.countLabel}>{selectedItemsCount} numbers</span>
        </span>
      )
    }

    const onShow = () => {
      const showDropdown = activeType === SearchTypesEnum.numbers && canShowChildDropdown

      setShow(showDropdown)
    }

    const showDebounce = debounce(onShow, 300)

    useEffect(() => {
      showDebounce()
    }, [canShowChildDropdown, activeType])

    return (
      <Dropdown
        ariaLabel={'NumbersElement'}
        parentsDropdownId={parentsDropdownId}
        iconButtonProps={{
          size: 'medium',
          fontSize: 20,
          ariaLabel: 'NumbersElement_trigger',
        }}
        show={show}
        triggerComponent={(open) => (
          <SearchElement
            icon={'phone'}
            type='Numbers'
            contentDefaultValue='select'
            warningContent={!open && !hasSelectedItems}
            removeCallback={() => {
              onRemove(SearchTypesEnum.numbers)
            }}
            isViewOnly={disableClosing}
            content={hasSelectedItems && <NumbersLabel />}
          />
        )}
        placement={'bottom-start'}
        sizeTriggerButton={'medium'}
        variantTriggerButton={'icon'}
        customComponent={() => <NumbersDropdownContent store={store} />}
        {...dropdownProps}
      />
    )
  }
)
